import { DEFINITIONS } from '../utils/constants';
import { getInlineSvg } from '../utils/helpers';
import { getTranslations } from '../utils/translations';

let activeTooltip = null;
let activeTooltipMarker = null;

/**
 * @description Creates tooltip content lazily when marker is clicked
 * @param {HTMLElement} context - Store locator container
 * @param {Object} store - Store data
 * @returns {HTMLElement} Tooltip element
 */
const createTooltipContent = (context, store) => {
  const translations = getTranslations(context);

  const markerTooltip = document.createElement('div');
  markerTooltip.classList.add(`${DEFINITIONS.slug}__marker-tooltip`);

  const tooltipContent = document.createElement('div');
  tooltipContent.classList.add(`${DEFINITIONS.slug}__marker-tooltip-content`);

  // Name
  if (store.name) {
    const nameEl = document.createElement('p');
    nameEl.className = `${DEFINITIONS.slug}__marker-name`;
    nameEl.textContent = store.name;
    tooltipContent.appendChild(nameEl);
  }

  // Address
  if (store.address) {
    const addressEl = document.createElement('div');
    addressEl.className = `${DEFINITIONS.slug}__marker-address`;
    const addressP = document.createElement('p');
    addressP.textContent = store.address;
    addressEl.appendChild(addressP);

    const addressParts = [store.postal_code, store.city, store.country].filter(Boolean).join(', ');
    if (addressParts) {
      const partsP = document.createElement('p');
      partsP.textContent = addressParts;
      addressEl.appendChild(partsP);
    }

    tooltipContent.appendChild(addressEl);
  }

  // Phone link
  if (store.phone) {
    const phoneLink = document.createElement('a');
    phoneLink.href = `tel:${store.phone}`;
    phoneLink.className = `${DEFINITIONS.slug}__marker-phone`;
    const phoneIcon = getInlineSvg('icon-phone', {
      className: `${DEFINITIONS.slug}__icon`,
      returnElement: true,
    });
    phoneLink.appendChild(phoneIcon);
    phoneLink.appendChild(document.createTextNode(store.phone));
    tooltipContent.appendChild(phoneLink);
  }

  // Email link
  // if (store.email) {
  //   const emailLink = document.createElement('a');
  //   emailLink.href = `mailto:${store.email}`;
  //   emailLink.className = `${DEFINITIONS.slug}__marker-email`;
  //   const emailIcon = getInlineSvg('icon-email', {
  //     className: `${DEFINITIONS.slug}__icon`,
  //     returnElement: true,
  //   });
  //   emailLink.appendChild(emailIcon);
  //   emailLink.appendChild(document.createTextNode(store.email));
  //   tooltipContent.appendChild(emailLink);
  // }

  // Website link
  if (store.website) {
    const websiteLink = document.createElement('a');
    websiteLink.href = store.website;
    websiteLink.target = '_blank';
    websiteLink.className = `${DEFINITIONS.slug}__marker-website`;
    const websiteIcon = getInlineSvg('icon-website', {
      className: `${DEFINITIONS.slug}__icon`,
      returnElement: true,
    });
    websiteLink.appendChild(websiteIcon);
    websiteLink.appendChild(document.createTextNode(store.website));
    tooltipContent.appendChild(websiteLink);
  }

  // Directions link
  const directionsLink = document.createElement('a');
  directionsLink.href = `https://www.google.com/maps/dir//${store.latitude},${store.longitude}`;
  directionsLink.target = '_blank';
  directionsLink.className = `${DEFINITIONS.slug}__marker-directions`;
  const directionsIcon = getInlineSvg('icon-directions', {
    className: `${DEFINITIONS.slug}__icon`,
    returnElement: true,
  });
  directionsLink.appendChild(directionsIcon);
  directionsLink.appendChild(document.createTextNode(translations?.store_locator?.get_directions || 'Get directions'));
  tooltipContent.appendChild(directionsLink);

  markerTooltip.appendChild(tooltipContent);
  return markerTooltip;
};

/**
 * @description Creates a lightweight map marker element (icon only, no tooltip)
 * @param {HTMLElement} context - Store locator container
 * @param {Object} store - Store data
 * @returns {HTMLElement} Marker element
 */
export const createMarkerElement = (context, { store }) => {
  const markerContent = document.createElement('div');
  markerContent.classList.add(`${DEFINITIONS.slug}__marker`);
  markerContent.dataset.id = store.uid;

  let markerIcon;

  if (store.marker) {
    markerIcon = document.createElement('img');
    markerIcon.classList.add(`${DEFINITIONS.slug}__marker-icon`);
    markerIcon.src = store.marker;
    markerIcon.alt = store.name || '';
  } else {
    markerIcon = getInlineSvg('icon-marker', { className: `${DEFINITIONS.slug}__marker-icon`, returnElement: true });
  }

  markerContent.appendChild(markerIcon);

  // Click handler - create tooltip on demand
  markerContent.addEventListener('click', (e) => {
    e.stopPropagation();

    // Close previous tooltip if exists
    if (activeTooltip && activeTooltipMarker !== markerContent) {
      activeTooltip.remove();
      if (activeTooltipMarker) {
        activeTooltipMarker.parentNode.style.zIndex = '';
      }
      activeTooltip = null;
      activeTooltipMarker = null;
    }

    // Toggle current tooltip
    if (activeTooltipMarker === markerContent) {
      // Close current
      if (activeTooltip) {
        activeTooltip.remove();
        markerContent.parentNode.style.zIndex = '';
        activeTooltip = null;
        activeTooltipMarker = null;
      }
    } else {
      // Create and show new tooltip
      const tooltip = createTooltipContent(context, store);
      tooltip.classList.add(DEFINITIONS.activeClass);
      markerContent.appendChild(tooltip);
      markerContent.parentNode.style.zIndex = '9999'; // Bring to front

      activeTooltip = tooltip;
      activeTooltipMarker = markerContent;
    }

    // Highlight list item
    const listItem = context.querySelector(`.${DEFINITIONS.slug}__list-item[data-id="${store.uid}"]`);
    if (listItem) {
      const activeItem = context.querySelector(`.${DEFINITIONS.slug}__list-item.${DEFINITIONS.activeClass}`);
      if (activeItem) activeItem.classList.remove(DEFINITIONS.activeClass);
      listItem.classList.add(DEFINITIONS.activeClass);

      const listContainer = context.querySelector(`.${DEFINITIONS.slug}__stores-container`);
      if (listContainer) {
        // Calculate scroll position considering virtual list
        const itemIndex = Array.from(listContainer.querySelectorAll(`.${DEFINITIONS.slug}__list-item`)).findIndex(
          (item) => item.dataset.id === store.uid,
        );

        if (itemIndex !== -1) {
          listContainer.scrollTo({
            top: itemIndex * 120, // Match virtualListState.itemHeight
            behavior: 'smooth',
          });
        }
      }
    }
  });

  return markerContent;
};

/**
 * @description Closes all active tooltips
 */
export const closeAllTooltips = () => {
  if (activeTooltip) {
    activeTooltip.remove();
    if (activeTooltipMarker) {
      activeTooltipMarker.parentNode.style.zIndex = '';
    }
    activeTooltip = null;
    activeTooltipMarker = null;
  }
};
